// Shared helpers for keeping outbound request bodies under the platform's
// upstream WAF body-size limit (see AWSManagedRulesCommonRuleSet /
// SizeRestrictions_BODY in infra/lib/gaianet-cf-waf-stack.ts). AWS WAF
// blocks - rather than truncates - request bodies over 8KB by default, and
// does so before the request reaches nginx/the API, so oversized requests
// fail with no application-side error or log to explain why.

// The WAF's actual hard limit.
const MAX_REQUEST_BODY_BYTES = 8192;
// Leave headroom under the hard limit for auth headers etc. counted
// against the same rule, and so batch sizing has some margin for error.
const SAFE_REQUEST_BODY_BYTES = 6144;

function byteSize(value) {
  const json = JSON.stringify(value);
  // jsdom (used by the Jest test environment) doesn't define TextEncoder.
  return typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(json).length
    : Buffer.byteLength(json, 'utf8');
}

// Greedily groups `items` into batches such that each batch, once mapped to
// its wire shape via `toPayload` and wrapped with `wrap`, stays under
// `maxBytes`. If a single mapped item alone exceeds the limit, it's placed
// in its own (oversized) batch - there's nothing more this helper can do to
// shrink one item.
function batchBySize(
  items,
  toPayload,
  { wrap = (payload) => payload, maxBytes = SAFE_REQUEST_BODY_BYTES } = {}
) {
  const batches = [];
  let current = [];

  for (const item of items) {
    const candidate = [...current, item];
    if (
      current.length > 0 &&
      byteSize(wrap(candidate.map(toPayload))) > maxBytes
    ) {
      batches.push(current);
      current = [item];
    } else {
      current = candidate;
    }
  }
  if (current.length > 0) {
    batches.push(current);
  }
  return batches;
}

export {
  batchBySize,
  byteSize,
  MAX_REQUEST_BODY_BYTES,
  SAFE_REQUEST_BODY_BYTES,
};
